import { createBrowserRouter, Navigate, type RouteObject } from 'react-router-dom';
import { AppLayout } from '@/components/layout/AppLayout';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { RequireAuth } from '@/components/layout/RequireAuth';
import { RoleSwitch } from '@/components/layout/RoleSwitch';
import { RoleRouter } from '@/pages/RoleRouter';
import { DashboardPage } from '@/pages/Dashboard';
import { LogInvitePage } from '@/pages/LogInvite';
import { BulkImportPage } from '@/pages/BulkImport';
import { InvitesPage } from '@/pages/Invites';
import { InviteDetailPage } from '@/pages/InviteDetail';
import { RatesPage } from '@/pages/Rates';
import { PayoutsPage } from '@/pages/Payouts';
import { ProspectsPage } from '@/pages/Prospects';
import { SearchIdeasPage } from '@/pages/SearchIdeas';
import { DiscoveryQueuePage } from '@/pages/DiscoveryQueue';
import { ProfilePage } from '@/pages/Profile';
import { InviterCreatorRequestsPage } from '@/pages/CreatorRequests';
import { InviterCreatorRequestDetailPage } from '@/pages/CreatorRequestDetail';
import { InviterCreatorRequestBatchDetailPage } from '@/pages/CreatorRequestBatchDetail';
import { SignInPage } from '@/pages/SignIn';
import { SignUpPage } from '@/pages/SignUp';
import { NotFoundPage } from '@/pages/NotFound';
import { AdminPayroll } from '@/pages/admin/AdminPayroll';
import { AdminPayouts } from '@/pages/admin/AdminPayouts';
import { AdminInviters } from '@/pages/admin/AdminInviters';
import { AdminInvites } from '@/pages/admin/AdminInvites';
import { AdminScreenshotAudit } from '@/pages/admin/AdminScreenshotAudit';
import { AdminNiches } from '@/pages/admin/AdminNiches';
import { AdminCreatorRequests } from '@/pages/admin/AdminCreatorRequests';
import { AdminCreatorRequestDetail } from '@/pages/admin/AdminCreatorRequestDetail';
import { AdminDiscovery } from '@/pages/admin/AdminDiscovery';
import { AdminEmail } from '@/pages/admin/AdminEmail';
import { AdminProspects } from '@/pages/admin/AdminProspects';
import { AdminChallenges } from '@/pages/admin/AdminChallenges';
import { AdminInsights, LegacyInsightsRedirect } from '@/pages/admin/insights/AdminInsights';
import { AdminHome } from '@/pages/admin/AdminHome';
import { AdminSetupTeams, AdminSetupCompensation, AdminSystem } from '@/pages/admin/AdminSetup';
import { AdminOutreacherProfile } from '@/pages/admin/AdminOutreacherProfile';
import { AdminOutreachAccounts } from '@/pages/admin/AdminOutreachAccounts';
import { AdminMessageQuotas } from '@/pages/admin/AdminMessageQuotas';
import { TeamScopeLock } from '@/hooks/useTeamScope';

/**
 * Team sections (Influencer, Vyral): the same admin pages, pinned to one
 * team (see useTeamScope and TEAM_SECTIONS in lib/teams).
 */
const influencer = (el: JSX.Element): JSX.Element => <TeamScopeLock team="INFLUENCER">{el}</TeamScopeLock>;
const vyral = (el: JSX.Element): JSX.Element => <TeamScopeLock team="VYRAL">{el}</TeamScopeLock>;

/**
 * The route tree, exported on its own so tests can mount it in a memory
 * router (src/__smoke__). The app uses it through `router` below.
 */
export const routes: RouteObject[] = [
  { path: '/sign-in/*', element: <SignInPage /> },
  { path: '/sign-up/*', element: <SignUpPage /> },

  // Root: ask /me, redirect to /admin or /portal
  {
    path: '/',
    element: (
      <RequireAuth>
        <RoleRouter />
      </RequireAuth>
    ),
  },

  // Outreacher surface
  {
    path: '/portal',
    element: (
      <RequireAuth>
        <RoleSwitch expect="outreacher">
          <AppLayout />
        </RoleSwitch>
      </RequireAuth>
    ),
    children: [
      { index: true, element: <DashboardPage /> },
      { path: 'log', element: <LogInvitePage /> },
      { path: 'bulk-import', element: <BulkImportPage /> },
      { path: 'prospects', element: <ProspectsPage /> },
      { path: 'search-ideas', element: <SearchIdeasPage /> },
      { path: 'discovery-queue', element: <DiscoveryQueuePage /> },
      { path: 'invites', element: <InvitesPage /> },
      { path: 'invites/:publicId', element: <InviteDetailPage /> },
      { path: 'creator-requests', element: <InviterCreatorRequestsPage /> },
      { path: 'creator-requests/:publicId', element: <InviterCreatorRequestDetailPage /> },
      {
        path: 'creator-request-batches/:publicId',
        element: <InviterCreatorRequestBatchDetailPage />,
      },
      { path: 'rates', element: <RatesPage /> },
      { path: 'payouts', element: <PayoutsPage /> },
      { path: 'profile', element: <ProfilePage /> },
    ],
  },

  // Admin surface
  {
    path: '/admin',
    element: (
      <RequireAuth>
        <RoleSwitch expect="admin">
          <AdminLayout />
        </RoleSwitch>
      </RequireAuth>
    ),
    children: [
      { index: true, element: <AdminHome /> },
      { path: 'payroll', element: <AdminPayroll /> },
      { path: 'payouts', element: <AdminPayouts /> },
      { path: 'outreachers', element: <AdminInviters /> },
      { path: 'outreachers/:publicId', element: <AdminOutreacherProfile /> },
      // Admin-only vault of outreachers' DM logins. No /portal counterpart, ever.
      { path: 'outreach-accounts', element: <AdminOutreachAccounts /> },
      { path: 'message-quotas', element: <AdminMessageQuotas /> },
      { path: 'invites', element: <AdminInvites /> },
      { path: 'screenshot-audit', element: <AdminScreenshotAudit /> },
      { path: 'insights', element: <AdminInsights /> },
      { path: 'niches', element: <AdminNiches /> },
      { path: 'setup/teams', element: <AdminSetupTeams /> },
      { path: 'setup/compensation', element: <AdminSetupCompensation /> },
      { path: 'system', element: <AdminSystem /> },
      // Influencer section
      { path: 'influencer', element: <Navigate to="/admin/influencer/requests" replace /> },
      { path: 'influencer/requests', element: influencer(<AdminCreatorRequests />) },
      { path: 'influencer/invites', element: influencer(<AdminInvites />) },
      { path: 'influencer/outreachers', element: influencer(<AdminInviters />) },
      { path: 'influencer/message-quotas', element: influencer(<AdminMessageQuotas />) },
      { path: 'influencer/payroll', element: influencer(<AdminPayroll />) },
      { path: 'influencer/performance', element: influencer(<AdminInsights />) },
      // Vyral section. No creator requests: none target Vyral.
      { path: 'vyral', element: <Navigate to="/admin/vyral/invites" replace /> },
      { path: 'vyral/invites', element: vyral(<AdminInvites />) },
      { path: 'vyral/outreachers', element: vyral(<AdminInviters />) },
      { path: 'vyral/message-quotas', element: vyral(<AdminMessageQuotas />) },
      { path: 'vyral/payroll', element: vyral(<AdminPayroll />) },
      { path: 'vyral/performance', element: vyral(<AdminInsights />) },
      // Old paths keep working.
      { path: 'inviters', element: <Navigate to="/admin/outreachers" replace /> },
      { path: 'rates', element: <Navigate to="/admin/setup/compensation?tab=rates" replace /> },
      { path: 'brackets', element: <Navigate to="/admin/setup/compensation?tab=follower-bonuses" replace /> },
      { path: 'categories', element: <Navigate to="/admin/setup/compensation?tab=creator-types" replace /> },
      { path: 'teams', element: <Navigate to="/admin/setup/teams" replace /> },
      { path: 'team-settings', element: <Navigate to="/admin/setup/teams?tab=rules" replace /> },
      { path: 'providers', element: <Navigate to="/admin/system?tab=providers" replace /> },
      { path: 'creator-requests', element: <AdminCreatorRequests /> },
      { path: 'creator-requests/:publicId', element: <AdminCreatorRequestDetail /> },
      { path: 'discovery/*', element: <AdminDiscovery /> },
      { path: 'email/*', element: <AdminEmail /> },
      { path: 'prospects', element: <AdminProspects /> },
      { path: 'challenges', element: <AdminChallenges /> },
      // The old reporting pages now live as Insights tabs.
      { path: 'analytics/inviters', element: <LegacyInsightsRedirect page="inviters" /> },
      { path: 'analytics/niches', element: <LegacyInsightsRedirect page="niches" /> },
      { path: 'analytics/efficiency', element: <LegacyInsightsRedirect page="efficiency" /> },
    ],
  },

  { path: '/404', element: <NotFoundPage /> },
  { path: '*', element: <Navigate to="/404" replace /> },
];

export const router = createBrowserRouter(routes);
